import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "./supabase";

interface Profile {
  name: string;
  role: string;
}

interface AuthState {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{
    user: User | null;
    profile: Profile | null;
    loading: boolean;
  }>({ user: null, profile: null, loading: true });

  async function loadProfile(user: User): Promise<Profile | null> {
    const { data } = await supabase
      .from("profiles")
      .select("name, role")
      .eq("id", user.id)
      .single();
    return data ?? null;
  }

  async function refresh() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setState({ user: null, profile: null, loading: false });
      return;
    }
    const profile = await loadProfile(user);
    setState({ user, profile, loading: false });
  }

  useEffect(() => {
    let mounted = true;

    async function applySession(user: User | null) {
      if (!mounted) return;
      if (!user) {
        setState({ user: null, profile: null, loading: false });
        return;
      }
      try {
        const profile = await loadProfile(user);
        if (!mounted) return;
        setState({ user, profile, loading: false });
      } catch (err) {
        console.error("Failed to load profile:", err);
        if (!mounted) return;
        setState({ user, profile: null, loading: false });
      }
    }

    // Use getSession() over getUser() for the initial read: it resolves
    // synchronously from the cached session in the storage adapter, so
    // the UI never hangs on a network round-trip. onAuthStateChange
    // fires immediately with INITIAL_SESSION too, but we don't rely on
    // that — explicit getSession + .catch keeps loading from latching
    // on if the storage adapter throws (the bug mode that stuck users
    // on "Loading...").
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => applySession(session?.user ?? null))
      .catch((err) => {
        console.error("supabase.auth.getSession() failed:", err);
        if (mounted) setState({ user: null, profile: null, loading: false });
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session?.user ?? null);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ ...state, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
